import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TypeaheadSuggestionsComponent } from './typeahead-suggestions.component';
import { Suggestion, TypeaheadService, TypeaheadSuggestionsState } from './typeahead.service';

export interface SuggestionsTemplateContext<T = Suggestion> {
  typeaheadstate: TypeaheadSuggestionsState<T>;
}

@Component({
  selector: 'rt-typeahead-inline',
  exportAs: 'rtTypeahead',
  template: `
    <ng-content></ng-content>

    @if (suggestionEnabled) {
      <ng-container
        *ngTemplateOutlet="
          suggestionsTemplate ? suggestionsTemplate : defaultSuggestionsTpl;
          context: suggestionsContext
        "
      ></ng-container>
    }

    <ng-template #defaultSuggestionsTpl let-typeaheadstate="typeaheadstate">
      <rt-typeahead-suggestions
        [spinnerTemplate]="spinnerTemplate"
        [stateName]="typeaheadstate.name"
        [stateValue]="typeaheadstate.value"
        [suggestions]="typeaheadstate.suggestions"
        [highlighted]="typeaheadstate.highlighted"
        (mousedown)="$event.stopImmediatePropagation(); $event.preventDefault()"
        (suggestionSelect)="select($event)"
      >
      </rt-typeahead-suggestions>
    </ng-template>
  `,
  host: {
    '(keydown.arrowUp)': 'selectPrev($event)',
    '(keydown.arrowDown)': 'selectNext($event)',
    '(keydown.enter)': 'selected.emit(service.selectHighlighted())',
  },
  providers: [TypeaheadService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, TypeaheadSuggestionsComponent],
})
export class TypeaheadInlineComponent {
  @Input() public suggestions: Suggestion[];
  @Input() public allSuggestionsExhausted: boolean;
  @Input() public value: string;
  @Input() public loading: boolean;
  @Input() public active: boolean = true;
  @Input() public suggestionEnabled: boolean = true;
  @Input() public suggestionsTemplate?: TemplateRef<SuggestionsTemplateContext>;
  @Input() public spinnerTemplate?: TemplateRef<any>;

  @Output() public selected: EventEmitter<Suggestion> = new EventEmitter();
  @Output() public changed: EventEmitter<string> = new EventEmitter();

  public suggestionsContext: SuggestionsTemplateContext;

  private value$ = new ReplaySubject<string>(1);
  private active$ = new ReplaySubject<boolean>(1);

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private service: TypeaheadService,
    private cdRef: ChangeDetectorRef,
    private elRef: ElementRef,
  ) {}

  public select(suggestion: Suggestion): void {
    this.service.select(suggestion);
    this.selected.emit(suggestion);
  }

  public selectNext($event: KeyboardEvent) {
    $event.preventDefault();
    this.service.highlightNext();
    this.elRef.nativeElement.querySelector('.highlighted').scrollIntoView({
      block: 'center',
    });
  }

  public selectPrev($event: KeyboardEvent) {
    $event.preventDefault();
    this.service.highlightPrev();
    this.elRef.nativeElement.querySelector('.highlighted').scrollIntoView({
      block: 'center',
    });
  }

  public ngOnInit(): void {
    this.service
      .setup({
        initial: {
          value: this.value,
          active: this.active,
        },
        streams: {
          value$: this.value$,
          active$: this.active$,
        },
      })
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(data => {
        this.suggestionsContext = { typeaheadstate: data };
        this.cdRef.detectChanges();
      });

    this.service.updateSuggestions(this.suggestions || []);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.suggestions && !changes.suggestions.firstChange) {
      this.service.updateSuggestions(this.suggestions, this.allSuggestionsExhausted);
    }

    if (changes.value) {
      this.value$.next(this.value);
    }

    if (changes.active) {
      this.active$.next(this.active);
    }

    if (changes.loading) {
      this.service.setState({
        ...this.service.state,
        loading: this.loading,
      });
    }
  }

  public ngOnDestroy(): void {
    this.service.cleanup();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
